/**
 * @Author Heriberto Monterrubio <heri185403@gmail.com>
 * @Date 2020-Sep-30
 *
 **/

import router from './../config/router';
import {Auth} from '../api';
import {loginResponseModel, loginRequestModel} from '../api/models';
import {createTimeWithIncrement, toFormData} from '@/core/utils';
import Constants from '@/core/constants';
import {createStrongPassword} from '../core/utils';
import * as Storage from '@/core/storage.js';

const accountModule = {
    namespaced: true,
    state: {
        loggedIn: false,
        user: null,
        authentication: loginResponseModel(),
        confirmado: '0',

        googleUserData: null,
        googleRegisterRequested: false,
        avatar: null,

        FB: null,
        facebookUserData: null,
        facebookRegisterRequested: false,
        facebookLoginData: null,

        googleOrFBLoginData: null,
        isAuthenticating: false,

        emailOrUsernameValid: false,
        userHasTryAvailable: null,

        isJustProfileUpdated: false
    },

    getters: {
        isGuestUser: state => {
            return state.user?.usuario === 'invitado';
        }
    },

    actions: {
        login({commit, dispatch}, request = loginRequestModel) {
            commit('logout');

            Auth.postLogin(request.username, request.password)
                .then(data => {
                    commit('loginSuccess', data);

                    if (request.username === 'invitado') {
                        commit('setSessionGuest', data.access_token);
                    }

                    dispatch('alerts/hideLoading', null, {root: true});
                    dispatch('getUserProfile');
                });
        },

        async getUserProfile({commit, dispatch}) {
            Auth.getUserProfile()
                .then(data => {
                    const userId = data.data.id;
                    localStorage.setItem('userId', userId);

                    commit('isConfirmado', data.data.confirmado);
                    dispatch('alerts/hideLoading', null, {root: true});
                    commit('userProfileSuccess', data.data);

                    if (router.currentRoute.name === 'login' || router.currentRoute.name === 'verify') {
                        router.push('/').catch(() => {
                        });
                    }
                })
                .catch(error => {
                    dispatch('alerts/hideLoading', null, {root: true});
                    console.log(error);
                });
        },

        async logout({commit, rootState}) {
            const keycloakBaseUrl = process.env.VUE_APP_KEYCLOAK_URL_BASE || 'http://localhost:8080';
            const realm = process.env.VUE_APP_KEYCLOAK_REALM || 'procu_ciudadano';
            const redirectUri = encodeURIComponent(window.location.origin + '/');

            // ⃣ Intentamos obtener el idToken desde Storage (encriptado)
            let token = Storage.getItem('idToken');

            //  Si no existe, fallback a Vuex KeycloakModule
            if (!token) {
                token = rootState.keycloakModule?.idToken || null;
            }

            //  Construcción de la URL de logout
            let url = null;

            if (token) {
                // Logout completo con id_token_hint
                url = `${keycloakBaseUrl}/realms/${realm}/protocol/openid-connect/logout` +
                    `?id_token_hint=${token}` +
                    `&post_logout_redirect_uri=${redirectUri}`;
            } else {
                console.warn('⚠️ No se encontró idToken. Ejecutando logout sin id_token_hint.');
                // Logout sin token (válido y evita error 400)
                url = `${keycloakBaseUrl}/realms/${realm}/protocol/openid-connect/logout` +
                    `?post_logout_redirect_uri=${redirectUri}`;
            }

            // 4️⃣ Antes de salir limpiamos Vuex + Tokens
            window.addEventListener('beforeunload', () => {
                commit('logout');
                commit('keycloakModule/CLEAR_TOKENS', null, {root: true});
            });

            // 5️⃣ Redirección inmediata
            window.location.href = url;

            // Microtask para asegurar flush
            await Promise.resolve();
        },

        registerUser({dispatch}, registerRequest) {
            Auth.postRegister(toFormData(registerRequest))
                .then(data => {


                    // Safe access para evitar TypeError
                    const usuario = data?.data?.usuario ?? 'usuario';


                    if (usuario !== 'usuario') {
                        dispatch(
                            'alerts/success',
                            `El usuario "${usuario}" fue creado correctamente.`,
                            {root: true}
                        );
                    }


                })
                .catch(err => {
                    console.error('❌ Error en registerUser:', err);
                    dispatch('alerts/error', 'Ocurrió un error al registrar el usuario.', {root: true});
                });
        },

        async checkAuthExpiration({commit, dispatch, state}) {
            if (!state.isAuthenticating) {
                const now = Math.floor(Date.now() / 1000);
                const expirationTime = parseInt(Storage.getItem('expiresAt'));

                if (now >= expirationTime) {
                    let authentication = Storage.getItem(Constants.KEY_AUTHENTICATION);
                    if (!authentication) {
                        return dispatch('logout', null);
                    }

                    state.isAuthenticating = true;

                    try {
                        const res = await Auth.refreshTokenWithKeycloak(Storage.getItem('refreshToken'));

                        const data = res.data;
                        const issuedAt = Math.floor(Date.now() / 1000);
                        const expiresAt = issuedAt + data.expires_in;

                        commit('keycloakModule/SET_TOKENS', {
                            accessToken: data.access_token,
                            refreshToken: data.refresh_token,
                            idToken: data.id_token,
                            token_expiration: data.expires_in,
                            expiresAt: expiresAt,
                        }, {root: true});

                    } catch (ex) {
                        console.error('❌ Error al obtener el token:', ex);
                        dispatch('alerts/error', 'Su sesión ha expirado, inicia nuevamente.', {root: true});
                        dispatch('logout', null);
                    }
                    state.isAuthenticating = false;
                }

            } else {
                let promise = new Promise((res) => {
                    setTimeout(() => res(true), 200);
                });

                await promise;

                if (state.isAuthenticating) {
                    await dispatch('account/checkAuthExpiration', null, {root: true});
                }
            }
        },

        checkLogin({commit, dispatch}) {
            // 1. Obtener datos (sin encriptar userKeycloak según tu config)
            let access_token = Storage.getItem('accessToken');
            let userKeycloak = Storage.getItem('userKeycloak', {encrypt: false}) || Storage.getItem('user', {encrypt: false});


            console.log('access_token',access_token);
            console.log("userKeycloak",userKeycloak);



            // Lógica de confirmación
            let objUser = access_token ? {confirmado: '1'} : {confirmado: '0'};
            commit('isConfirmado', objUser.confirmado);

            // 2. VERIFICACIÓN
            if (access_token && userKeycloak && objUser.confirmado === '1') {
                // El usuario tiene sesión válida.
                // CORRECCIÓN: NO hacemos router.push aquí.
                // Dejamos que Vue Router respete la URL actual (ej: /orders/list).
                commit('hasAuthentication', userKeycloak, access_token);
            } else {
                // 3. NO LOGUEADO (O NO CONFIRMADO)
                let currentRoute = window.location.pathname;

                // Si el usuario NO tiene token, verificamos si debemos sacarlo de aquí
                if (!access_token) {
                    // Solo redirigir al Home si NO estamos ya en una ruta pública (login, home, callback, etc)
                    const publicRoutes = ['/home', '/process', '/callback', '/register', '/reset', '/verify'];
                    const isPublic = publicRoutes.some(route => currentRoute.includes(route));

                    if (!isPublic) {
                        router.push({name: 'home'}).catch(() => {
                        });
                    }
                }
                // Si tiene token pero falta confirmar cuenta
                else if (objUser.confirmado === '0') {
                    dispatch('alerts/error', 'Usuario no validado, valide su cuenta para iniciar sesión', {root: true});
                }
            }
        },

        async postUserProfile({commit, dispatch}, userData) {
            dispatch('alerts/showLoading', 'Actualizando información del usuario', {root: true});
            await dispatch('account/checkAuthExpiration', null, {root: true});

            Auth.updateProfile(toFormData(userData))
                .then(data => {
                    dispatch('alerts/hideLoading', null, {root: true});
                    dispatch('alerts/success', 'Información del usuario actualizado correctamente.', {root: true});
                    commit('userProfileSuccess', data.data);
                })
                .catch(error => {
                    dispatch('alerts/error', error.message, {root: true});
                    console.log(error);
                });
        },

        async setNewUserPassword({dispatch}, request) {
            dispatch('alerts/showLoading', 'Registrando tu nueva contraseña', {root: true});

            Auth.newPassword(toFormData(request))
                .then(data => {
                    dispatch('alerts/hideLoading', null, {root: true});
                    dispatch('alerts/success', data.message, {root: true});
                    router.push({name: 'login'});
                })
                .catch(error => {
                    console.log(JSON.stringify(error));
                    dispatch('alerts/error', error.message, {root: true});
                });
        },

        async getTryAvailable({dispatch, commit}) {
            dispatch('alerts/showLoading', 'Obteniendo información de la cuenta.', {root: true});
            await dispatch('account/checkAuthExpiration', null, {root: true});

            Auth.verifyTryAvailable()
                .then(data => {
                    dispatch('alerts/hideLoading', null, {root: true});
                    commit('setUserHasTrayAvailable', data.tryAvailable);
                })
                .catch(error => {
                    console.log(JSON.stringify(error));
                    commit('setUserHasTrayAvailable', true);
                });
        },
    },

    mutations: {
        loginRequest(state) {
            state.loggedIn = false;
            state.user = null;
        },


        loginSuccess(state, data) {
            // Guardamos tokens encriptados (encrypt default = true)
            Storage.setItem(Constants.KEY_TOKEN, data.access_token); // accessToken
            Storage.setItem('accessToken', data.access_token);
            Storage.setItem('refreshToken', data.refresh_token);
            Storage.setItem('idToken', data.id_token);
            Storage.setItem('expiresAt', String(Math.floor(Date.now() / 1000) + data.expires_in));

            Storage.setItem(Constants.KEY_TOKEN_EXPIRATION, createTimeWithIncrement(data.expires_in).toString());
            Storage.setItem(Constants.KEY_AUTHENTICATION, JSON.stringify(data)); // si quieres encriptar auth, puedes usar encrypt:true explícito

            if (data.userId) {
                // userId como número en localStorage (directo)
                localStorage.setItem('userId', data.userId);
            }

            state.loggedIn = true;
            state.authentication = data;
            state.googleOrFBLoginData = null;
        },


        hasAuthentication(state, user, authentication) {
            state.loggedIn = true;
            state.user = user ?? null;
            state.accessToken = authentication;
        },

        isConfirmado(state, confirmado) {
            state.confirmado = confirmado;
        },

        logout(state) {
            // Limpiamos tokens (encriptados)
            Storage.removeItem(Constants.KEY_AUTHENTICATION);
            Storage.removeItem(Constants.KEY_TOKEN);
            Storage.removeItem(Constants.KEY_TOKEN_EXPIRATION);
            Storage.removeItem('accessToken');
            Storage.removeItem('refreshToken');
            Storage.removeItem('idToken');
            Storage.removeItem('expiresAt');

            // user sin encriptar
            Storage.removeItem(Constants.KEY_USER);
            Storage.removeItem('userKeycloak'); // si lo pusiste sin encriptar

            Storage.removeItem(Constants.KEY_TOKEN_GUEST);
            Storage.removeItem(Constants.KEY_LAST_FORM_URL);
            Storage.removeItem(Constants.KEY_LAST_PROCESS_EXECUTED);
            Storage.removeItem(Constants.KEY_SKIP_VERIFICATION_CODE);
            Storage.removeItem(Constants.KEY_LAST_FISCAL_CONTACT);
            Storage.removeItem('userId');

            state.loggedIn = false;
            state.authentication = loginRequestModel();
            state.user = null;
        },


        userProfileSuccess(state, data) {
            // user almacenado COMO OBJETO (no encriptado según tu requerimiento)
            Storage.setItem(Constants.KEY_USER, data, {encrypt: false});
            localStorage.setItem('userId', data.id);

            state.user = data;
            state.userId = data.id;
            state.isJustProfileUpdated = true;
        },

        requestRegisterWithGoogle(state, userData) {
            state.googleRegisterRequested = true;
            state.googleUserData = userData;
        },

        requestRegisterWithFacebook(state, userData) {
            state.facebookRegisterRequested = true;
            state.facebookUserData = userData;
        },

        registerUserSuccess(state, userRegistered) {
            if (state.googleRegisterRequested || state.facebookRegisterRequested) {
                state.googleOrFBLoginData = loginRequestModel();
                state.googleOrFBLoginData.username = userRegistered.usuario;
                state.googleOrFBLoginData.password = createStrongPassword(
                    userRegistered.usuario,
                    state.googleRegisterRequested ? userRegistered.email : userRegistered.usuario
                );

                state.googleRegisterRequested = false;
                state.googleUserData = null;

                state.facebookRegisterRequested = false;
                state.facebookUserData = null;
            }
        },

        cancelRegisterWithGoogleOrFacebook(state) {
            state.googleRegisterRequested = false;
            state.googleUserData = null;

            state.facebookRegisterRequested = false;
            state.facebookUserData = null;
        },

        saveFacebookSession(state, FB) {
            state.FB = FB;
        },

        setSessionGuest(state, sessionToken) {
            Storage.setItem(Constants.KEY_TOKEN_GUEST, sessionToken);
        },

        setAswordPreten(state, payload) {
            state.aswordPre = payload;
        },

        setValidationUsernameEmail(state, payload) {
            state.emailOrUsernameValid = payload;
        },

        setUserHasTrayAvailable(state, hasTryAvailable) {
            state.userHasTryAvailable = hasTryAvailable;
        },
    },
};

export default accountModule;